import { ref, computed, watch } from 'vue';
import { useFormValidation } from './useFormValidation';
import { isNotEmpty, isValidEmail, isValidPhone, isValidDate } from '@/utils/validation';

/**
 * Cancellation form management composable
 * 
 * @param {Object} options - Configuration options
 * @param {Object} options.initialValues - Initial form values (optional)
 * @param {Function} options.onReasonChange - Callback when reason changes (optional)
 * @returns {Object} - Form management utilities
 */
export function useCancellationForm(options = {}) {
  const { 
    initialValues = {},
    onReasonChange = null
  } = options;
  
  // Form data
  const reason = ref(initialValues.reason || '');
  const realtorInfo = ref(initialValues.realtorInfo || {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: ''
  });
  const dynamicFields = ref(initialValues.dynamicFields || {});
  const callbackTime = ref(initialValues.callbackTime || {
    date: '',
    time: ''
  });
  const isWinBackSelected = ref(initialValues.isWinBackSelected || false);
  
  // Computed values
  const today = computed(() => {
    return new Date().toISOString().split('T')[0];
  });
  
  const showWinBackOption = computed(() => {
    // Only show consultation option for specific reasons
    const consultationEligibleReasons = [
      'new_insurer',
      'financial',
      'mortgage_change'
    ];
    return consultationEligibleReasons.includes(reason.value);
  });
  
  // Watch for reason changes
  watch(reason, (newReason) => {
    // If changing to a reason that doesn't support win-back, deselect it
    if (isWinBackSelected.value && !showWinBackOption.value) {
      isWinBackSelected.value = false;
    }
    
    // Call the callback if provided
    if (onReasonChange && typeof onReasonChange === 'function') {
      onReasonChange(newReason);
    }
  });
  
  // Create validation schema based on selected reason
  const createValidationSchema = () => {
    const schema = {
      // Common validations
      reason: {
        validator: isNotEmpty,
        errorMessage: 'Please select a cancellation reason'
      }
    };
    
    // Add validations specific to the selected reason
    if (reason.value === 'selling_home') {
      // Make all realtor fields optional
      schema.realtorFirstName = {
        validator: (val) => true, // Optional
        errorMessage: 'Realtor first name is required'
      };
      schema.realtorLastName = {
        validator: (val) => true, // Optional
        errorMessage: 'Realtor last name is required'
      };
      schema.realtorEmail = {
        validator: (val) => !val || isValidEmail(val),
        errorMessage: 'Please enter a valid email address'
      };
      schema.realtorPhoneNumber = {
        validator: (val) => !val || isValidPhone(val),
        errorMessage: 'Please enter a valid phone number'
      };
    }
    else if (reason.value === 'new_insurer') {
      schema.newInsurerName = {
        validator: isNotEmpty,
        errorMessage: 'New insurer name is required'
      };
      schema.newPolicyNumber = {
        validator: (val) => true, // Optional
        errorMessage: 'New policy number is required'
      };
      schema.newPolicyDate = {
        validator: (val) => !val || isValidDate(val),
        errorMessage: 'Please enter a valid date'
      };
    }
    else if (reason.value === 'ownership_change') {
      schema.newOwnerName = {
        validator: (val) => true, // Optional
        errorMessage: 'New owner name is required'
      };
      schema.transferDate = {
        validator: (val) => !val || isValidDate(val),
        errorMessage: 'Please enter a valid date'
      };
      schema.ownershipChangeReason = {
        validator: isNotEmpty,
        errorMessage: 'Please select a reason for ownership change'
      };
    }
    else if (reason.value === 'risk_reduction') {
      schema.riskReductionDescription = {
        validator: isNotEmpty,
        errorMessage: 'Description is required'
      };
      schema.riskReductionDate = {
        validator: (val) => !val || isValidDate(val),
        errorMessage: 'Please enter a valid date'
      };
    }
    else if (reason.value === 'mortgage_change') {
      schema.previousMortgageCompany = {
        validator: isNotEmpty,
        errorMessage: 'Previous mortgage company is required'
      };
      schema.mortgagePayoffDate = {
        validator: (val) => !val || isValidDate(val),
        errorMessage: 'Please enter a valid date'
      };
      schema.loanNumber = {
        validator: (val) => true, // Optional
        errorMessage: 'Loan number is required'
      };
    }
    else if (reason.value === 'moving') {
      schema.newAddress = {
        validator: (val) => true, // Optional
        errorMessage: 'New address is required'
      };
      schema.movingDate = {
        validator: (val) => !val || isValidDate(val),
        errorMessage: 'Please enter a valid date'
      };
      schema.isPropertySold = {
        validator: (val) => val === true || val === false,
        errorMessage: 'Please select if the property is sold'
      };
    }
    else if (reason.value === 'financial') {
      schema.hardshipType = {
        validator: isNotEmpty,
        errorMessage: 'Please select a hardship type'
      };
      schema.hardshipDuration = {
        validator: (val) => true, // Optional
        errorMessage: 'Please select expected duration'
      };
      schema.alternativeCoverage = {
        validator: (val) => true, // Optional
        errorMessage: 'Alternative coverage needs is required'
      };
    }
    else if (reason.value === 'consolidation') {
      schema.consolidatedPolicyNumber = {
        validator: (val) => true, // Optional
        errorMessage: 'Policy number is required'
      };
      schema.insuranceProvider = {
        validator: isNotEmpty,
        errorMessage: 'Insurance provider is required'
      };
      schema.consolidationEffectiveDate = {
        validator: (val) => !val || isValidDate(val),
        errorMessage: 'Please enter a valid date'
      };
    }
    
    // Add win-back specific validations if selected
    if (isWinBackSelected.value) {
      schema.callbackDate = {
        validator: isNotEmpty,
        errorMessage: 'Callback date is required'
      };
      schema.callbackTime = {
        validator: isNotEmpty,
        errorMessage: 'Callback time is required'
      };
    }
    
    return schema;
  };
  
  // Create and configure form validation
  const validation = useFormValidation({
    schema: createValidationSchema(),
    validateOnChange: true
  });
  
  // Update validation schema when reason changes
  watch(reason, () => {
    validation.resetForm(getFormValues());
  });
  
  // Update validation schema when win-back selection changes
  watch(isWinBackSelected, () => {
    validation.resetForm(getFormValues());
  });
  
  // Helper to get all form values in a flat structure for validation
  const getFormValues = () => {
    const values = {
      reason: reason.value
    };
    
    // Add realtor info
    if (reason.value === 'selling_home') {
      values.realtorFirstName = realtorInfo.value.firstName;
      values.realtorLastName = realtorInfo.value.lastName;
      values.realtorEmail = realtorInfo.value.email;
      values.realtorPhoneNumber = realtorInfo.value.phoneNumber;
    }
    
    // Add dynamic fields based on reason
    if (reason.value === 'new_insurer' && dynamicFields.value.newInsurer) {
      values.newInsurerName = dynamicFields.value.newInsurer.name;
      values.newPolicyNumber = dynamicFields.value.newInsurer.policyNumber;
      values.newPolicyDate = dynamicFields.value.newInsurer.effectiveDate;
    }
    else if (reason.value === 'ownership_change' && dynamicFields.value.ownershipChange) {
      values.newOwnerName = dynamicFields.value.ownershipChange.newOwnerName;
      values.transferDate = dynamicFields.value.ownershipChange.transferDate;
      values.ownershipChangeReason = dynamicFields.value.ownershipChange.reason;
    }
    else if (reason.value === 'risk_reduction' && dynamicFields.value.riskReduction) {
      values.riskReductionDescription = dynamicFields.value.riskReduction.description;
      values.riskReductionDate = dynamicFields.value.riskReduction.date;
    }
    else if (reason.value === 'mortgage_change' && dynamicFields.value.mortgageChange) {
      values.previousMortgageCompany = dynamicFields.value.mortgageChange.previousCompany;
      values.mortgagePayoffDate = dynamicFields.value.mortgageChange.payoffDate;
      values.loanNumber = dynamicFields.value.mortgageChange.loanNumber;
    }
    else if (reason.value === 'moving' && dynamicFields.value.moving) {
      values.newAddress = dynamicFields.value.moving.newAddress;
      values.movingDate = dynamicFields.value.moving.movingDate;
      values.isPropertySold = dynamicFields.value.moving.isPropertySold;
    }
    else if (reason.value === 'financial' && dynamicFields.value.financial) {
      values.hardshipType = dynamicFields.value.financial.hardshipType;
      values.hardshipDuration = dynamicFields.value.financial.expectedDuration;
      values.alternativeCoverage = dynamicFields.value.financial.alternativeCoverage;
    }
    else if (reason.value === 'consolidation' && dynamicFields.value.consolidation) {
      values.consolidatedPolicyNumber = dynamicFields.value.consolidation.policyNumber;
      values.insuranceProvider = dynamicFields.value.consolidation.provider;
      values.consolidationEffectiveDate = dynamicFields.value.consolidation.effectiveDate;
    }
    
    // Add win-back data
    if (isWinBackSelected.value) {
      values.callbackDate = callbackTime.value.date;
      values.callbackTime = callbackTime.value.time;
    }
    
    return values;
  };
  
  // Helpers to update form data
  const updateReason = (newReason) => {
    reason.value = newReason;
  };
  
  const updateRealtorInfo = (field, value) => {
    if (!field) return; // Guard against undefined field
    
    // Handle case where field is an object (entire realtorInfo)
    if (typeof field === 'object') {
      realtorInfo.value = { ...realtorInfo.value, ...field };
      
      // Update validation fields individually for each property
      if (reason.value === 'selling_home') {
        try {
          Object.entries(field).forEach(([key, val]) => {
            const capitalizedField = key.charAt(0).toUpperCase() + key.slice(1);
            validation.setFieldValue(`realtor${capitalizedField}`, val);
          });
        } catch (err) {
          console.error('Error updating realtor fields:', err, { field });
        }
      }
      return;
    }
    
    // Handle normal case where field is a string
    realtorInfo.value = { ...realtorInfo.value, [field]: value };
    
    // Update validation
    if (reason.value === 'selling_home') {
      try {
        const capitalizedField = field.charAt(0).toUpperCase() + field.slice(1);
        validation.setFieldValue(`realtor${capitalizedField}`, value);
      } catch (err) {
        console.error('Error updating realtor field:', err, { field, value });
      }
    }
  };
  
  const updateDynamicField = (category, field, value) => {
    if (!category || !field) {
      console.error('Invalid parameters for updateDynamicField', { category, field });
      return;
    }
    
    if (!dynamicFields.value[category]) {
      dynamicFields.value[category] = {};
    }
    
    dynamicFields.value[category] = { 
      ...dynamicFields.value[category], 
      [field]: value 
    };
    
    // Map to validation fields
    if (reason.value === category || 
        (reason.value === 'selling_home' && category === 'realtorInfo') ||
        (showWinBackOption.value && category === 'callbackTime')) {
      
      // Determine the validation field name based on category and field
      let validationField = '';
      
      switch(category) {
        case 'newInsurer':
          if (field === 'name') validationField = 'newInsurerName';
          else if (field === 'policyNumber') validationField = 'newPolicyNumber';
          else if (field === 'effectiveDate') validationField = 'newPolicyDate';
          break;
        case 'ownershipChange':
          if (field === 'newOwnerName') validationField = 'newOwnerName';
          else if (field === 'transferDate') validationField = 'transferDate';
          else if (field === 'reason') validationField = 'ownershipChangeReason';
          break;
        case 'riskReduction':
          if (field === 'description') validationField = 'riskReductionDescription';
          else if (field === 'date') validationField = 'riskReductionDate';
          break;
        case 'mortgageChange':
          if (field === 'previousCompany') validationField = 'previousMortgageCompany';
          else if (field === 'payoffDate') validationField = 'mortgagePayoffDate';
          else if (field === 'loanNumber') validationField = 'loanNumber';
          break;
        case 'moving':
          if (field === 'newAddress') validationField = 'newAddress';
          else if (field === 'movingDate') validationField = 'movingDate';
          else if (field === 'isPropertySold') validationField = 'isPropertySold';
          break;
        case 'financial':
          if (field === 'hardshipType') validationField = 'hardshipType';
          else if (field === 'expectedDuration') validationField = 'hardshipDuration';
          else if (field === 'alternativeCoverage') validationField = 'alternativeCoverage';
          break;
        case 'consolidation':
          if (field === 'policyNumber') validationField = 'consolidatedPolicyNumber';
          else if (field === 'provider') validationField = 'insuranceProvider';
          else if (field === 'effectiveDate') validationField = 'consolidationEffectiveDate';
          break;
        default:
          validationField = field;
      }
      
      if (validationField) {
        try {
          validation.setFieldValue(validationField, value);
        } catch (err) {
          console.error('Error updating validation field:', err, { validationField, value });
        }
      }
    }
  };
  
  const updateCallbackTime = (field, value) => {
    if (!field) {
      console.error('Invalid field parameter for updateCallbackTime', { field });
      return;
    }
    
    callbackTime.value = { ...callbackTime.value, [field]: value };
    
    // Update validation if win-back is selected
    if (isWinBackSelected.value) {
      try {
        const capitalizedField = field.charAt(0).toUpperCase() + field.slice(1);
        validation.setFieldValue(`callback${capitalizedField}`, value);
      } catch (err) {
        console.error('Error updating callback time field:', err, { field, value });
      }
    }
  };
  
  const toggleWinBack = () => {
    isWinBackSelected.value = !isWinBackSelected.value;
  };
  
  // Validate form for next step
  const validateForm = () => {
    // Populate form values
    return validation.validateAll();
  };
  
  // Reset form
  const resetForm = (newValues = {}) => {
    reason.value = newValues.reason || '';
    realtorInfo.value = newValues.realtorInfo || {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: ''
    };
    dynamicFields.value = newValues.dynamicFields || {};
    callbackTime.value = newValues.callbackTime || {
      date: '',
      time: ''
    };
    isWinBackSelected.value = newValues.isWinBackSelected || false;
    
    // Reset validation
    validation.resetForm();
  };
  
  return {
    // Form data
    reason,
    realtorInfo,
    dynamicFields,
    callbackTime,
    isWinBackSelected,
    
    // Computed
    today,
    showWinBackOption,
    
    // Validation
    errors: validation.errors,
    isValid: validation.isValid,
    validateForm,
    
    // Update helpers
    updateReason,
    updateRealtorInfo,
    updateDynamicField,
    updateCallbackTime,
    toggleWinBack,
    
    // Form management
    resetForm
  };
} 